import { memo, useCallback } from "react";
import { generatePath, Link, useNavigate } from "react-router";

import type { Snack, SnackSettings } from "@/lib/gql/__generated__/graphql";
import { useCopyToClipboard } from "@/lib/react-use/use-copy-to-clipboard";

import { NEW_ITEM_DUPLICATED_ITEM_ID_PARAMS, PATHS } from "@/routes/paths";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import { Button } from "@/components/ui/button/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import EditDocument from "@/components/ui/icon/edit-document";
import LinkIcon from "@/components/ui/icon/link";
import ThreeDotsHorizontal from "@/components/ui/icon/three-dots-horizontal";
import { Image } from "@/components/ui/image/image";

type Props = {
	hotelID: string;

	snack: Snack;
	snackSettings: SnackSettings;

	onDelete: ({ uuid, title }: { uuid: string; title: string }) => Promise<void>;
};

const getSnackPageURL = ({
	type,
	slug,
	uuid,
	subDomain,
	baseDomain,
}: { type: "STANDARD" | "LP" } & Pick<Snack, "slug" | "uuid"> & Pick<SnackSettings, "subDomain" | "baseDomain">) =>
	`https://${subDomain}.${baseDomain}/${type === "STANDARD" ? "snack" : "LP"}/${slug || uuid}`;

export const DesignSnackListItem = memo(({ hotelID, snack, snackSettings, onDelete }: Props) => {
	const { thumbnail, title, uuid, slug } = snack;

	const editPagePath = generatePath(PATHS.HOTEL.RESERVATION_PAGE.SNACK.EDIT.UUID, { hotelID, uuid });
	const duplicatePagePath = `${generatePath(PATHS.HOTEL.RESERVATION_PAGE.SNACK.EDIT.NEW, { hotelID })}?${NEW_ITEM_DUPLICATED_ITEM_ID_PARAMS}=${snack.uuid}`;

	const navigate = useNavigate();
	const { copy } = useCopyToClipboard();

	// コピーするボタンを表示するかどうか
	const isShowCopyURLButton = !!snackSettings.subDomain && !!snackSettings.baseDomain;

	const handleClickCopyStandardURL = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();

			const url = getSnackPageURL({
				type: "STANDARD",
				slug,
				uuid,
				subDomain: snackSettings.subDomain,
				baseDomain: snackSettings.baseDomain,
			});

			copy(url);
			notifyUserEventInfo({
				title: "通常版のURLをコピーしました",
			});
		},
		[copy, slug, snackSettings.baseDomain, snackSettings.subDomain, uuid],
	);
	const handleClickCopyLP_URL = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();

			const url = getSnackPageURL({
				type: "LP",
				slug,
				uuid,
				subDomain: snackSettings.subDomain,
				baseDomain: snackSettings.baseDomain,
			});

			copy(url);
			notifyUserEventInfo({
				title: "LP版のURLをコピーしました",
			});
		},
		[copy, slug, snackSettings.baseDomain, snackSettings.subDomain, uuid],
	);

	const handleClickCopyButton = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();

			navigate(duplicatePagePath);
		},
		[duplicatePagePath, navigate],
	);

	const handleClickDeleteButton = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onDelete({ uuid, title });
		},
		[onDelete, title, uuid],
	);

	const handleClick = useCallback(() => {
		navigate(editPagePath);
	}, [editPagePath, navigate]);

	return (
		<div
			onClick={handleClick}
			className="group flex items-center justify-between gap-x-6 py-3 hover:cursor-pointer"
		>
			<div className="flex items-center gap-x-6">
				<Image src={thumbnail} className="h-16 w-16 rounded object-cover" />
				<p className="group-hover:underline">{title}</p>
			</div>
			<div className="flex items-center gap-x-6">
				<Link to={editPagePath} onClick={(e) => e.stopPropagation()} viewTransition>
					<Button type="button" variant="input-ish" size="icon" tooltip="編集">
						<EditDocument size="sm" />
					</Button>
				</Link>

				{isShowCopyURLButton && (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant={"input-ish"}
								size={"icon"}
								onClick={(e) => e.stopPropagation()}
							>
								<LinkIcon size="sm" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent className="w-52">
							<div className="grid space-y-1 text-left">
								<Button variant={"no-outline"} textAlign={"left"} onClick={handleClickCopyStandardURL}>
									通常版のURLをコピー
								</Button>

								<Button variant={"no-outline"} textAlign={"left"} onClick={handleClickCopyLP_URL}>
									LP版のURLをコピー
								</Button>
							</div>
						</DropdownMenuContent>
					</DropdownMenu>
				)}

				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button
							variant={"input-ish"}
							size={"icon"}
							onClick={(e) => e.stopPropagation()}
						>
							<ThreeDotsHorizontal size="sm" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent className="w-40">
						<div className="grid space-y-1 text-left">
							<Button variant={"no-outline"} textAlign={"left"} onClick={handleClickCopyButton}>
								複製する
							</Button>

							<Button variant={"no-outline"} textAlign={"left"} onClick={handleClickDeleteButton}>
								<p className="font-medium text-error-600">削除する</p>
							</Button>
						</div>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</div>
	);
});

DesignSnackListItem.displayName = "DesignSnackListItem";
